import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Search, 
  BookOpen, 
  ExternalLink, 
  FileText, 
  Scale, 
  Globe,
  TrendingUp,
  Sparkles,
  Clock,
  CheckCircle2,
  Filter,
  Loader2,
  Download,
  Bookmark,
  Share2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Knowledge({ user, cabinet, onLogout }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [sortBy, setSortBy] = useState("relevance");
  const [filters, setFilters] = useState({
    sources: ["legifrance", "eurlex"],
    documentTypes: []
  });
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('lawliet_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const loadBookmarks = async () => {
      setBookmarksLoading(true);
      try {
        const res = await axios.get(`${API}/knowledge/bookmarks`, { headers: authHeaders() });
        setBookmarks(res.data || []);
      } catch {
        // silently fail — bookmarks are non-critical
      } finally {
        setBookmarksLoading(false);
      }
    };
    loadBookmarks();
  }, []);

  const toggleBookmark = async (doc) => {
    const existing = bookmarks.find(b => b.document_id === doc.id && b.source === doc.source);

    if (existing) {
      // Remove bookmark
      try {
        await axios.delete(`${API}/knowledge/bookmarks/${existing.id}`, { headers: authHeaders() });
        setBookmarks(prev => prev.filter(b => b.id !== existing.id));
        toast.success("Favori retiré");
      } catch {
        toast.error("Erreur lors de la suppression");
      }
    } else {
      // Add bookmark
      try {
        const res = await axios.post(`${API}/knowledge/bookmarks`, {
          document_id: doc.id,
          source: doc.source,
          title: doc.title,
          url: doc.url || null,
        }, { headers: authHeaders() });
        setBookmarks(prev => [res.data, ...prev]);
        toast.success("Ajouté aux favoris");
      } catch (err) {
        if (err.response?.status === 409) {
          toast.info("Déjà dans vos favoris");
        } else {
          toast.error("Erreur lors de l'ajout");
        }
      }
    }
  };

  const isBookmarked = (doc) =>
    bookmarks.some(b => b.document_id === doc.id && b.source === doc.source);

  const handleShare = (doc) => {
    const text = `${doc.title} — ${doc.reference || ''} (${doc.source})`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Référence copiée dans le presse-papiers");
    }).catch(() => {
      toast.error("Impossible de copier");
    });
  };

  const sources = [
    { id: "legifrance", name: "Légifrance", icon: Scale, color: "blue", description: "Code civil, jurisprudence française" },
    { id: "eurlex", name: "EUR-Lex", icon: Globe, color: "purple", description: "Législation UE" },
    { id: "cjue", name: "CJUE", icon: BookOpen, color: "green", description: "Jurisprudence européenne" }
  ];

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Veuillez entrer une requête");
      return;
    }

    setSearching(true);
    setResults(null);

    try {
      const token = localStorage.getItem('lawliet_token');
      
      const response = await axios.post(
        `${API}/knowledge/search`,
        {
          query: searchQuery,
          sources: filters.sources,
          max_results: 20,
          include_citations: true,
          language: "fr"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResults(response.data);
      toast.success(`${response.data.total_results} résultats trouvés`);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Erreur lors de la recherche");
    } finally {
      setSearching(false);
    }
  };

  const toggleSource = (sourceId) => {
    setFilters(prev => ({
      ...prev,
      sources: prev.sources.includes(sourceId)
        ? prev.sources.filter(s => s !== sourceId)
        : [...prev.sources, sourceId]
    }));
  };

  const getSourceBadge = (source) => {
    const config = {
      legifrance: { label: "Légifrance", className: "bg-blue-100 text-blue-800" },
      eurlex: { label: "EUR-Lex", className: "bg-purple-100 text-purple-800" },
      cjue: { label: "CJUE", className: "bg-green-100 text-green-800" }
    };
    return config[source] || config.legifrance;
  };

  const getDocumentTypeBadge = (type) => {
    const config = {
      code: { label: "Code", className: "bg-slate-100 text-slate-800" },
      loi: { label: "Loi", className: "bg-indigo-100 text-indigo-800" },
      jurisprudence: { label: "Jurisprudence", className: "bg-amber-100 text-amber-800" },
      directive: { label: "Directive UE", className: "bg-purple-100 text-purple-800" },
      reglement: { label: "Règlement UE", className: "bg-purple-100 text-purple-800" }
    };
    return config[type] || { label: type, className: "bg-slate-100 text-slate-800" };
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-blue-900 flex items-center gap-3">
            <BookOpen className="h-8 w-8" />
            Knowledge - Recherche Juridique
          </h1>
          <p className="text-slate-600 mt-1">
            Accès instantané à Légifrance, EUR-Lex et la jurisprudence française et européenne
          </p>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sources.map((source) => {
            const Icon = source.icon;
            return (
              <Card key={source.id} className={`border-l-4 border-l-${source.color}-500`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-${source.color}-100`}>
                        <Icon className={`h-5 w-5 text-${source.color}-600`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{source.name}</h3>
                        <p className="text-xs text-slate-600">{source.description}</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={filters.sources.includes(source.id)}
                      onCheckedChange={() => toggleSource(source.id)}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Search Bar */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  placeholder='Ex: "responsabilité contractuelle article 1240" ou "RGPD consentement cookies"'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 h-12 text-base"
                />
              </div>
              <Button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="bg-blue-600 hover:bg-blue-700 h-12 px-8"
              >
                {searching ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Recherche...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Rechercher
                  </>
                )}
              </Button>
            </div>

            {results && (
              <div className="mt-4 flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 text-slate-700">
                  <Sparkles className="h-4 w-4 text-blue-600" />
                  <span className="font-medium">{results.total_results} résultats</span>
                  <span className="text-slate-500">•</span>
                  <span className="text-slate-600">{results.search_time.toFixed(2)}s</span>
                </div>
                <div className="flex gap-1">
                  {results.sources_used.map((source) => (
                    <Badge key={source} variant="outline" className="text-xs">
                      {source}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Results List */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Résultats de Recherche</CardTitle>
                    <CardDescription>{results.total_results} documents juridiques</CardDescription>
                  </div>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="knowledge-sort-select"
                  >
                    <option value="relevance">Par pertinence</option>
                    <option value="score_desc">Score élevé d'abord</option>
                    <option value="score_asc">Score faible d'abord</option>
                    <option value="date_desc">Plus récents d'abord</option>
                    <option value="date_asc">Plus anciens d'abord</option>
                    <option value="source">Par source</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  {results.results.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun résultat trouvé</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {[...results.results].sort((a, b) => {
                        const da = a.document, db = b.document;
                        if (sortBy === "score_desc") return b.relevance_score - a.relevance_score;
                        if (sortBy === "score_asc") return a.relevance_score - b.relevance_score;
                        if (sortBy === "date_desc") return new Date(db.publication_date || 0) - new Date(da.publication_date || 0);
                        if (sortBy === "date_asc") return new Date(da.publication_date || 0) - new Date(db.publication_date || 0);
                        if (sortBy === "source") return (da.source || "").localeCompare(db.source || "");
                        return 0;
                      }).map((result, idx) => {
                        const doc = result.document;
                        const sourceBadge = getSourceBadge(doc.source);
                        const typeBadge = getDocumentTypeBadge(doc.document_type);
                        
                        return (
                          <Card
                            key={idx}
                            className={`cursor-pointer hover:shadow-md transition-all ${
                              selectedDocument?.id === doc.id ? 'border-blue-500 border-2' : ''
                            }`}
                            onClick={() => setSelectedDocument(doc)}
                          >
                            <CardContent className="p-4 space-y-3">
                              {/* Header */}
                              <div className="flex items-start justify-between">
                                <div className="flex gap-2">
                                  <Badge className={sourceBadge.className}>
                                    {sourceBadge.label}
                                  </Badge>
                                  <Badge className={typeBadge.className}>
                                    {typeBadge.label}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-slate-600">
                                  <TrendingUp className="h-3 w-3" />
                                  {result.relevance_score.toFixed(0)}%
                                </div>
                              </div>

                              {/* Title */}
                              <h3 className="font-semibold text-slate-900 text-sm leading-tight">
                                {doc.title}
                              </h3>

                              {/* Excerpt */}
                              {result.matched_excerpts && result.matched_excerpts.length > 0 && (
                                <p className="text-xs text-slate-600 line-clamp-2">
                                  {result.matched_excerpts[0]}
                                </p>
                              )}

                              {/* Meta */}
                              <div className="flex items-center gap-3 text-xs text-slate-500">
                                {doc.publication_date && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {new Date(doc.publication_date).toLocaleDateString('fr-FR')}
                                  </div>
                                )}
                                {doc.reference && (
                                  <span className="truncate">{doc.reference}</span>
                                )}
                              </div>

                              {/* Keywords */}
                              {doc.keywords && doc.keywords.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {doc.keywords.slice(0, 3).map((kw, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">
                                      {kw}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Document Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Aperçu du Document</CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedDocument ? (
                  <div className="text-center py-24 text-slate-500">
                    <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Sélectionnez un document pour voir les détails</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <div className="space-y-6 pr-4">
                      {/* Document Header */}
                      <div>
                        <h2 className="text-xl font-bold text-slate-900 mb-3">
                          {selectedDocument.title}
                        </h2>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <Badge className={getSourceBadge(selectedDocument.source).className}>
                            {getSourceBadge(selectedDocument.source).label}
                          </Badge>
                          <Badge className={getDocumentTypeBadge(selectedDocument.document_type).className}>
                            {getDocumentTypeBadge(selectedDocument.document_type).label}
                          </Badge>
                          {selectedDocument.jurisdiction && (
                            <Badge variant="outline">{selectedDocument.jurisdiction}</Badge>
                          )}
                        </div>
                      </div>

                      {/* Référence */}
                      {selectedDocument.reference && (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                          <p className="text-sm font-semibold text-blue-900 mb-1">Référence</p>
                          <p className="text-sm text-slate-700">{selectedDocument.reference}</p>
                        </div>
                      )}

                      {/* Summary */}
                      {selectedDocument.summary && (
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-2">Résumé</h3>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {selectedDocument.summary}
                          </p>
                        </div>
                      )}

                      {/* Content */}
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Contenu</h3>
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                            {selectedDocument.content}
                          </p>
                        </div>
                      </div>

                      {/* Keywords */}
                      {selectedDocument.keywords && selectedDocument.keywords.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-2">Mots-clés</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedDocument.keywords.map((kw, i) => (
                              <Badge key={i} variant="outline">{kw}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 pt-4 border-t">
                        <Button variant="outline" className="flex-1" asChild>
                          <a href={selectedDocument.official_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Voir sur le site officiel
                          </a>
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => toggleBookmark(selectedDocument)}
                          data-testid="bookmark-btn"
                          className={isBookmarked(selectedDocument) ? "text-amber-600 border-amber-300" : ""}
                        >
                          <Bookmark className={`h-4 w-4 ${isBookmarked(selectedDocument) ? "fill-amber-500" : ""}`} />
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare(selectedDocument)}
                          data-testid="share-btn"
                        >
                          <Share2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!results && !searching && (
          <Card className="bg-slate-50">
            <CardContent className="py-16 text-center">
              <div className="max-w-md mx-auto">
                <BookOpen className="h-16 w-16 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Recherchez dans la base de connaissances juridique
                </h3>
                <p className="text-sm text-slate-600 mb-6">
                  Accédez instantanément au Code civil, à la jurisprudence française et européenne, 
                  ainsi qu'à la législation de l'Union Européenne.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <Badge variant="outline">Code civil</Badge>
                  <Badge variant="outline">RGPD</Badge>
                  <Badge variant="outline">Jurisprudence</Badge>
                  <Badge variant="outline">Directives UE</Badge>
                  <Badge variant="outline">CJUE</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
